import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { API_ENDPOINTS } from '../config/api';

const VideosContainer = styled.div`
  padding: 6rem 2rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const VideoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
`;

const VideoCard = styled(motion.article)`
  background: ${props => props.theme.surface};
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px ${props => props.theme.border};
`;

const VideoThumbnail = styled.div`
  position: relative;
  width: 100%;
  padding-top: 56.25%; /* 16:9 Aspect Ratio */
  background: ${props => props.theme.surface};
  cursor: pointer;

  .video-wrapper {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
  }

  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .play-button {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 60px;
    height: 60px;
    background: rgba(0, 0, 0, 0.7);
    border-radius: 50%;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;

    &::before {
      content: '';
      width: 0;
      height: 0;
      border-style: solid;
      border-width: 10px 0 10px 20px;
      border-color: transparent transparent transparent white;
      margin-left: 5px;
    }
  }

  &:hover .play-button {
    background: rgba(0, 0, 0, 0.8);
  }
`;

const VideoContent = styled.div`
  padding: 1.5rem;

  h2 {
    margin: 0 0 1rem;
    font-size: 1.5rem;
    color: ${props => props.theme.text};
  }

  p {
    color: ${props => props.theme.textSecondary};
    margin-bottom: 1rem;
  }
`;

const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const Tag = styled.span`
  background: ${props => props.theme.primary}22;
  color: ${props => props.theme.primary};
  padding: 0.25rem 0.75rem;
  border-radius: 16px;
  font-size: 0.875rem;
`;

const LoadingMessage = styled.p`
  text-align: center;
  margin-top: 2rem;
  color: ${props => props.theme.textSecondary};
`;

const ErrorMessage = styled.p`
  text-align: center;
  margin-top: 2rem;
  color: ${props => props.theme.error};
`;

const Videos = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeVideo, setActiveVideo] = useState(null);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.VIDEOS);
        if (!response.ok) {
          throw new Error('Failed to fetch videos');
        }
        const data = await response.json();
        setVideos(data);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  if (loading) {
    return (
      <VideosContainer>
        <LoadingMessage>Loading videos...</LoadingMessage>
      </VideosContainer>
    );
  }

  if (error) {
    return (
      <VideosContainer>
        <ErrorMessage>Error: {error}</ErrorMessage>
      </VideosContainer>
    );
  }

  return (
    <VideosContainer>
      <h1>Videos</h1>
      <VideoGrid>
        {videos.map((video) => (
          <VideoCard
            key={video.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <VideoThumbnail onClick={() => setActiveVideo(activeVideo === video.id ? null : video.id)}>
              {activeVideo === video.id ? (
                <div className="video-wrapper">
                  <iframe
                    src={`https://www.youtube.com/embed/${video.embedId}?autoplay=1&rel=0`}
                    title={video.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                    allowFullScreen
                  />
                </div>
              ) : (
                <>
                  <img src={video.thumbnail} alt={video.title} />
                  <div className="play-button" />
                </>
              )}
            </VideoThumbnail>
            <VideoContent>
              <h2>{video.title}</h2>
              <p>{video.description}</p>
              <TagContainer>
                {video.tags.map((tag) => (
                  <Tag key={tag}>{tag}</Tag>
                ))}
              </TagContainer>
            </VideoContent>
          </VideoCard>
        ))}
      </VideoGrid>
    </VideosContainer>
  );
};

export default Videos;
